import React, { useState, useEffect, forwardRef } from "react";
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
} from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

// --- Utility Mappings (Centralized) ---
const getStatusName = (statusCode) => {
    switch (statusCode) {
        case 0: return 'In Progress';
        case 1: return 'Pending Payment';
        case 2: return 'Completed';
        case 5: return 'Cancelled';
        default: return 'Other';
    }
};

const getOrderTypeName = (typeCode) => {
    switch (typeCode) {
        case 0: return 'In-store';
        case 1: return 'Pickup';
        case 2: return 'Kiosk';
        case 3: return 'Online';
        default: return 'Other';
    }
};

const payTypeMapReverse = { 0: 'Cash', 1: 'Gift Card', 2: 'Online', 3: 'Credit Card', 4: 'Other' };

// --- Data Processing Utilities (Refactored for flat Order Array) ---
const processEmployeeData = (allOrders) => {
    // allOrders is now expected to be a single flat array of Order documents.
    if (!allOrders || !Array.isArray(allOrders) || allOrders.length === 0) {
        return { summary: [], detailed: {} };
    }
    
    // FIX: Filter the single array based on status
    const completedOrders = allOrders.filter(order => order.oStatus === 2);
    const cancelOrders = allOrders.filter(order => order.oStatus === 5);


    const employeeMap = {};
    
    const initializeEmployee = (id, name) => ({
        name: name,
        totalSales: 0,
        totalTips: 0,
        orderCount: 0,
        voidCount: 0,
        orderTypeCounts: { 'In-store': 0, 'Pickup': 0, 'Kiosk': 0, 'Online': 0, 'Other': 0 },
        paymentTypeCounts: { 'Cash': 0, 'Credit Card': 0, 'Online': 0, 'Gift Card': 0, 'Other': 0 },
        orders: []
    });

    // Helper to process a list of orders (Completed OR Cancelled)
    const processOrderList = (ordersList) => {
        console.log(ordersList);
        ordersList?.forEach(order => {
            const total = order.total ?? 0;
            const tip = order.Tip ?? 0;
            const status = getStatusName(order.oStatus);
            const orderType = getOrderTypeName(order.type);

            // Assume the first client in the order array is the responsible employee
            // clientID and cName are now top-level on the OrderSchema, but we use the
            // order.orders[0] structure for compatibility with existing payload structure
            const employeeOrderer = order.orders?.[0]; 

            if (employeeOrderer) {
                const employeeId = employeeOrderer.clientID;
                const employeeName = employeeOrderer.cName;

                if (!employeeMap[employeeId]) {
                    employeeMap[employeeId] = initializeEmployee(employeeId, employeeName);
                }

                employeeMap[employeeId].orderCount++;
                employeeMap[employeeId].orders.push({ 
                    orderNo: order.orderNo, 
                    total, 
                    tip, 
                    status, 
                    orderType,
                    paymentType: order.payment?.[0] ? payTypeMapReverse[order.payment[0].payType] : 'N/A',
                });

                if (status === 'Completed') {
                    employeeMap[employeeId].totalSales += total;
                    employeeMap[employeeId].totalTips += tip;
                    
                    employeeMap[employeeId].orderTypeCounts[orderType]++;
                    
                    order.payment?.forEach(payment => {
                        const payTypeName = payTypeMapReverse[payment.payType] || 'Other';
                        employeeMap[employeeId].paymentTypeCounts[payTypeName]++;
                    });

                } else if (status === 'Cancelled') {
                    employeeMap[employeeId].voidCount++;
                }
            }
        });
    };
    
    // Process both lists
    processOrderList(completedOrders);
    processOrderList(cancelOrders);


    // Final Summary Array Calculation
    const employeeSummary = Object.values(employeeMap).map(employee => ({
        ...employee,
        avgOrderValue: employee.orderCount > 0 ? (employee.totalSales / employee.orderCount) : 0,
        tipRate: employee.totalSales > 0 ? (employee.totalTips / employee.totalSales) * 100 : 0,
    }));

    return { summary: employeeSummary, detailed: employeeMap };
};

// --- Recharts Data Processor ---
const processChartData = (summaryData) => {
    return summaryData.map(e => ({
        name: e.name,
        'Total Sales': parseFloat(e.totalSales.toFixed(2)),
        'Total Tips': parseFloat(e.totalTips.toFixed(2)),
    }));
};

// --- Main Component ---
export const EmployeeReportPage = forwardRef(({ rawData }, ref) => {
    const [reportData, setReportData] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

useEffect(() => {
    const fetchAndProcessData = () => {
        setIsLoading(true);

        let sourceArray = null; // This will hold the flat array of Order documents

        // 1. Check prop data first
        if (rawData && rawData.length > 0) {
            sourceArray = rawData;
        } 
        
        // 2. If prop data is flat or incomplete, try loading from sessionStorage
        if (!sourceArray || sourceArray.length === 0) {
            const stored = sessionStorage.getItem("employeeReportData");
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    
                    // FIX: We expect parsed.rawData to be the array of Orders now (or fall back to the whole parsed object)
                    sourceArray = parsed.rawData.orders || parsed; 
                    
                } catch (err) {
                    console.error("Failed to parse employee report data:", err);
                    sourceArray = [];
                }
            }
        }

        if (sourceArray && sourceArray.length > 0) {
            const { summary, detailed } = processEmployeeData(sourceArray);
            setReportData({ summary, detailed });
            setChartData(processChartData(summary));
        } else {
            setReportData(null);
        }
        setIsLoading(false);
    };

    fetchAndProcessData();
}, [rawData]);

    if (isLoading) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }} ref={ref}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (!reportData || reportData.summary.length === 0) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }} ref={ref}>
                <Paper elevation={3} sx={{ p: 3, my: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        No employee data found for the selected range.
                    </Typography>
                </Paper>
            </Container>
        );
    }
    
    const { summary, detailed } = reportData;

    // --- Dynamic Header Generation ---
    const firstEmployee = summary[0]; 
    const orderTypeHeaders = Object.keys(firstEmployee.orderTypeCounts || {});
    const paymentTypeHeaders = Object.keys(firstEmployee.paymentTypeCounts || {});

    return (
        <Container maxWidth="xl" sx={{ py: 4 }} ref={ref}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Employee Performance Report
            </Typography>

            {/* Visual Summary (Graph) and Operational Metrics - Top Row */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 4 }}>
                
                {/* Section 1: Sales/Tips Comparison Graph (Recharts) */}
                <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '450px', backgroundColor: '#f5faff' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Sales & Tip Comparison</Typography>
                    <Box sx={{ minHeight: '300px', height: '100%', maxHeight: '400px' }}>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={chartData}
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                                <Legend />
                                {/* Two bars for Sales and Tips */}
                                <Bar dataKey="Total Sales" fill="#1976d2" />
                                <Bar dataKey="Total Tips" fill="#2e7d32" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>

                {/* Section 2: Operational Metrics & Productivity Summary */}
                <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '450px', backgroundColor: '#e1f5fe' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Productivity Summary</Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#c8e6c9' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Orders</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Voided</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Avg Order ($)</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Tip Rate (%)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {summary.map((employee, index) => (
                                    <TableRow 
                                        key={index}
                                        sx={{ 
                                            '&:nth-of-type(odd)': { backgroundColor: '#f8f8f8' }, 
                                            '&:hover': { backgroundColor: '#e0e0e0', cursor: 'pointer' } 
                                        }}
                                    >
                                        <TableCell>{employee.name}</TableCell>
                                        <TableCell align="right">{employee.orderCount}</TableCell>
                                        <TableCell align="right" sx={{ color: employee.voidCount > 0 ? 'error.main' : 'inherit' }}>{employee.voidCount}</TableCell>
                                        <TableCell align="right">${employee.avgOrderValue.toFixed(2)}</TableCell>
                                        <TableCell align="right" sx={{ color: employee.tipRate > 5 ? 'success.main' : 'warning.main', fontWeight: 'bold' }}>{employee.tipRate.toFixed(2)}%</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            {/* Comparison Tables - Second Row (Full Width) */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 4 }}>
                {/* Section 3: Order Type Comparison */}
                <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '450px', backgroundColor: '#fff3e0' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Order Type Performance</Typography>
                    <TableContainer component={Paper} elevation={1} sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#ffcc80' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                                    {orderTypeHeaders.map(type => (
                                        <TableCell key={type} align="right" sx={{ fontWeight: 'bold' }}>{type}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {summary.map((employee, index) => (
                                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fff8e8' } }}>
                                        <TableCell>{employee.name}</TableCell>
                                        {orderTypeHeaders.map((type) => (
                                            <TableCell key={type} align="right">{employee.orderTypeCounts[type]}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Section 4: Payment Type Comparison */}
                <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '450px', backgroundColor: '#f3e5f5' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Payment Type Handled</Typography>
                    <TableContainer component={Paper} elevation={1} sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#e1bee7' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                                    {paymentTypeHeaders.map(type => (
                                        <TableCell key={type} align="right" sx={{ fontWeight: 'bold' }}>{type}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {summary.map((employee, index) => (
                                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fbe9e7' } }}>
                                        <TableCell>{employee.name}</TableCell>
                                        {paymentTypeHeaders.map((type) => (
                                            <TableCell key={type} align="right">{employee.paymentTypeCounts[type]}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            {/* Section 5: Detailed Employee Order Log (Fully Open) */}
            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3, backgroundColor: '#e3f2fd' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Detailed Order Log by Employee</Typography>
                    
                    {Object.keys(detailed).map((employeeId) => {
                        const employee = detailed[employeeId];
                        return (
                            <Box key={employeeId} sx={{ my: 3 }}>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary.dark" sx={{ mb: 1, borderBottom: '1px solid #90caf9' }}>
                                    {employee.name} (Total Orders: {employee.orderCount})
                                </Typography>
                                <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 1 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#bbdefb' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Order No.</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Tip</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {employee.orders.map((order, orderIndex) => (
                                                <TableRow 
                                                    key={orderIndex}
                                                    sx={{ 
                                                        '&:nth-of-type(odd)': { backgroundColor: '#f8f8f8' }, 
                                                        '&:hover': { backgroundColor: '#e0e0e0', cursor: 'pointer' } 
                                                    }}
                                                >
                                                    <TableCell>{order.orderNo}</TableCell>
                                                    <TableCell>{order.orderType}</TableCell>
                                                    <TableCell align="right">${order.total.toFixed(2)}</TableCell>
                                                    <TableCell align="right">${order.tip.toFixed(2)}</TableCell>
                                                    <TableCell>{order.status}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        );
                    })}
                </Paper>
            </Box>
        </Container>
    );
});